import { useRef, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, CheckCircle2, Download, FileSpreadsheet,
  RefreshCw, Upload, X,
} from "lucide-react";
import { API } from "../../lib/api";
import { fmtN } from "../../lib/format";

export default function UploadModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => { setFile(null); setError(null); setResult(null); setUploading(false); };
  const close = () => { reset(); onClose(); };
  const handleFile = (f) => {
    setError(null); setResult(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) { setError("File harus berformat .xlsx (Excel)"); return; }
    if (f.size > 20 * 1024 * 1024) { setError("Ukuran file maksimal 20 MB"); return; }
    setFile(f);
  };
  const doUpload = async () => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const form = new FormData(); form.append("file", file);
      const res = await axios.post(`${API}/sales/upload`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setResult(res.data);
      setTimeout(() => { onSuccess(); }, 800);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload gagal");
    } finally { setUploading(false); }
  };

  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={close} data-testid="upload-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: "var(--navy)" }}>
              <FileSpreadsheet size={18} style={{ verticalAlign: "-3px", marginRight: 8 }} /> Upload Dataset Baru
            </h2>
            <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "var(--muted)" }}>
              Sistem akan validasi kolom, hilangkan duplikat (sales+tanggal+NPSN), lalu reload dashboard otomatis.
            </p>
          </div>
          <button className="icon-btn" onClick={close} data-testid="upload-close"><X size={18} /></button>
        </div>

        {!result && (<>
          <div className={`drop-zone ${dragOver ? "drag" : ""} ${file ? "has-file" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
            data-testid="upload-dropzone">
            <input ref={inputRef} type="file" accept=".xlsx" onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: "none" }} data-testid="upload-input" />
            {!file ? (<>
              <Upload size={36} color="var(--primary)" />
              <p style={{ margin: "10px 0 4px 0", fontWeight: 600, color: "var(--navy)" }}>Klik atau drag file Excel ke sini</p>
              <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>Format: .xlsx · maks 20 MB</p>
            </>) : (<>
              <FileSpreadsheet size={36} color="var(--good)" />
              <p style={{ margin: "10px 0 4px 0", fontWeight: 600, color: "var(--navy)" }}>{file.name}</p>
              <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>{(file.size / 1024).toFixed(1)} KB · siap di-upload</p>
            </>)}
          </div>
          {error && <div className="alert err" data-testid="upload-error"><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 18, alignItems: "center" }}>
            <a href={`${API}/sales/upload/template`} className="link" target="_blank" rel="noreferrer">
              <Download size={12} /> Download template
            </a>
            <div style={{ flex: 1 }} />
            <button className="btn btn-secondary" onClick={close} disabled={uploading}>Batal</button>
            <button className="btn btn-primary" onClick={doUpload} disabled={!file || uploading} data-testid="upload-submit">
              {uploading ? <><RefreshCw size={14} className="spin" /> Mengupload...</> : <><Upload size={14} /> Upload & Replace</>}
            </button>
          </div>
        </>)}

        {result && (
          <div className="result-panel" data-testid="upload-success">
            <div style={{ textAlign: "center" }}>
              <CheckCircle2 size={48} color="var(--good)" />
              <h3 style={{ margin: "12px 0 4px 0", color: "var(--navy)" }}>Berhasil!</h3>
              <p style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}>
                Dataset di-update dengan {result.rows} baris bersih.
              </p>
            </div>
            {result.dedup && (
              <div className="dedup-box">
                <div className="dedup-title">Hasil Anti-Duplikasi</div>
                <div className="dedup-grid">
                  <div><span>Baris Upload</span><b>{fmtN(result.dedup.uploaded_rows)}</b></div>
                  <div><span>Duplikat Internal Dihapus</span><b style={{color:"var(--bad)"}}>{fmtN(result.dedup.internal_duplicates_removed)}</b></div>
                  <div><span>Setelah Dedup</span><b style={{color:"var(--good)"}}>{fmtN(result.dedup.after_internal_dedup)}</b></div>
                  <div><span>Overlap dgn Master</span><b>{fmtN(result.dedup.overlap_with_previous_master)}</b></div>
                  <div><span>Baris Baru vs Master</span><b style={{color:"var(--accent)"}}>{fmtN(result.dedup.new_unique_rows_vs_previous)}</b></div>
                </div>
              </div>
            )}
            <dl className="result-grid">
              <dt>File</dt><dd>{result.filename}</dd>
              <dt>Sales</dt><dd>{result.preview.nama_sales.join(", ")}</dd>
              <dt>Tujuan Sales</dt><dd>{result.preview.tujuan_sales.join(", ")}</dd>
              <dt>Status</dt><dd>{result.preview.status_sekolah.join(", ")}</dd>
            </dl>
            <button className="btn btn-primary" onClick={close} style={{ width: "100%", marginTop: 12 }} data-testid="upload-done">
              Lihat Dashboard Terbaru
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
